import type { StoreConfig } from '../modules/Config';

type RetailerOptionListProps = {
  options: StoreConfig[];
  selectedKey: string;
  onSelect?: (key: string) => void;
};

export function RetailerOptionList({
  options,
  selectedKey,
  onSelect,
}: RetailerOptionListProps) {
  const Tag = onSelect ? 'button' : 'div';

  return (
    <div className="flex flex-col gap-3">
      {options.map((option) => {
        const isSelected = selectedKey === option.retailer_key;
        return (
          <Tag
            key={option.retailer_key}
            data-testid={`retailer-option-${option.retailer_key}`}
            onClick={onSelect ? () => onSelect(option.retailer_key) : undefined}
            className={`flex items-center gap-4 px-5 py-4 rounded-xl border-[1.5px] text-left transition-all duration-150 ${
              isSelected
                ? 'border-primary bg-primary-pale'
                : 'border-rule bg-surface hover:border-ink-faint'
            }`}
          >
            <div
              className={`w-11 h-11 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-surface' : 'bg-paper'
              }`}
            >
              {option.logo_url ? (
                <img
                  src={option.logo_url}
                  alt={option.retailer_name}
                  className="w-7 h-7 object-contain"
                />
              ) : (
                <svg
                  className="w-7 h-7 text-ink-muted"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
                  />
                </svg>
              )}
            </div>

            <div className="flex-1">
              <span className="block font-semibold text-[15px] text-ink">
                {option.label}
              </span>
              <span className="block text-[13px] text-ink-muted mt-0.5">
                {option.domain}
              </span>
            </div>

            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                isSelected ? 'bg-primary' : 'border-2 border-rule'
              }`}
            >
              {isSelected && (
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                >
                  <path d="M9.707 3.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L5 6.586l3.293-3.293a1 1 0 011.414 0z" />
                </svg>
              )}
            </div>
          </Tag>
        );
      })}
    </div>
  );
}
